import React, { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle, Envelope } from "@phosphor-icons/react";
import { customerAuth } from "../lib/customerAuth";

/**
 * /customer/forgot-password
 * -------------------------
 * Always returns a generic message regardless of whether the email
 * exists. Never displays a raw reset link to unauthenticated callers.
 */
export default function CustomerForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.(); if (busy) return;
    setBusy(true);
    try {
      await customerAuth.forgot(email.trim().toLowerCase());
    } catch { /* generic response — swallow anything network-y */ }
    finally { setBusy(false); setDone(true); }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="customer-forgot-page">
      <div className="w-full max-w-md rounded-2xl bg-white shadow-lg border border-slate-200">
        <div className="px-6 py-5 border-b border-slate-100">
          <div className="text-xl font-semibold text-slate-900">Reset your password</div>
          <div className="text-xs text-slate-500 mt-1">
            Enter the email you use with the customer portal. If it's linked to an account, we'll generate reset instructions.
          </div>
        </div>
        <div className="px-6 py-5 space-y-3">
          {done ? (
            <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800 flex items-start gap-2" data-testid="customer-forgot-generic">
              <CheckCircle size={16} weight="bold" className="mt-[1px] shrink-0" />
              <span>If an eligible account exists, password-reset instructions have been generated.</span>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3">
              <div>
                <label className="text-xs font-medium text-slate-700 block mb-1" htmlFor="cfp-email">Email</label>
                <input
                  id="cfp-email"
                  type="email"
                  autoComplete="username"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="customer-forgot-email"
                  className="w-full rounded-lg border border-slate-300 text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              <button
                type="submit"
                disabled={busy}
                data-testid="customer-forgot-submit"
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-60"
              >
                <Envelope size={16} weight="bold" /> {busy ? "Submitting…" : "Send reset instructions"}
              </button>
            </form>
          )}
        </div>
        <div className="px-6 py-3 border-t border-slate-100 text-xs">
          <Link to="/customer/login" className="text-emerald-700 hover:underline" data-testid="customer-forgot-back-link">← Back to sign in</Link>
        </div>
      </div>
    </div>
  );
}
